// Which onboarding films this recruiter has watched. One small store shared by every
// place the player is mounted (greeting, wizard header, left panel), read once from the
// profile and written back on each completion. Progress therefore follows the recruiter
// across devices and does not vanish with the browser.
import { useSyncExternalStore } from "react";

import { getOnboardingFilms, markOnboardingFilm } from "@/components/copilot/api";

let watched: string[] = [];
let loaded = false;
// Whether the profile has answered: "failed" lets a caller go on without it.
let status: "loading" | "ready" | "failed" = "loading";
let loading: Promise<void> | null = null;
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function loadWatched(): Promise<void> {
  if (loaded) return Promise.resolve();
  if (!loading) {
    loading = getOnboardingFilms()
      .then((ids) => {
        watched = ids;
        loaded = true;
        status = "ready";
        emit();
      })
      .catch(() => {
        // Without the profile answer the films simply show as unwatched.
        loading = null;
        status = "failed";
        emit();
      });
  }
  return loading;
}

export function useWatchedFilms(): string[] {
  return useSyncExternalStore(subscribe, () => watched, () => watched);
}

export function useWatchedStatus() {
  return useSyncExternalStore(subscribe, () => status, () => status);
}

// Optimistic: the check appears at once; the profile catches up.
export function markWatched(film: string) {
  if (watched.includes(film)) return;
  watched = [...watched, film];
  emit();
  void markOnboardingFilm(film).catch(() => {});
}

// For tests and sign-out.
export function resetWatchedForTests(ids: string[] = []) {
  watched = ids;
  loaded = false;
  loading = null;
  status = "loading";
  emit();
}
